import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useNetworkBrowse } from './NetworkBrowseContext';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import {
  cancelRouteOverlapRun,
  createRouteOverlapRun,
  getRouteOverlapRun,
} from '../services/routeOverlap/routeOverlapApi';
import {
  CreateRouteOverlapRunRequest,
  RouteOverlapRun,
  RouteOverlapRunStatus,
} from '../services/routeOverlap/types';

const POLL_INTERVAL_MS = 1200;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
const DEFAULT_TOLERANCE_METERS = 30;

interface RouteOverlapContextValue {
  isModalOpen: boolean;
  selectedCandidateRouteId: string | null;
  setSelectedCandidateRouteId: (routeId: string | null) => void;
  openModal: (routeId?: string | null) => void;
  closeModal: () => void;
  run: RouteOverlapRun | null;
  status: RouteOverlapRunStatus | 'idle';
  isStarting: boolean;
  error: string | null;
  toleranceMetersDefault: number;
  selectedComparisonRouteId: string | null;
  focusComparisonRoute: (routeId: string | null) => void;
  startRun: (request: CreateRouteOverlapRunRequest) => Promise<RouteOverlapRun | null>;
  cancelRun: () => Promise<void>;
  clearError: () => void;
}

const RouteOverlapContext = createContext<RouteOverlapContextValue | undefined>(undefined);

function isTerminalStatus(status: RouteOverlapRunStatus): boolean {
  return status === 'complete' || status === 'failed' || status === 'cancelled';
}

export function RouteOverlapProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { feedId, version, scenarioId } = useNetworkBrowse();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCandidateRouteId, setSelectedCandidateRouteId] = useState<string | null>(null);
  const [selectedComparisonRouteId, setSelectedComparisonRouteId] = useState<string | null>(null);
  const [run, setRun] = useState<RouteOverlapRun | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toleranceMetersDefault, setToleranceMetersDefault] = useState(DEFAULT_TOLERANCE_METERS);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef<string | null>(null);
  const runFeedIdRef = useRef<string | null>(null);
  const runStartTimeRef = useRef<number | null>(null);
  const prevStatusRef = useRef<RouteOverlapRunStatus | 'idle'>('idle');
  const pollFailureCountRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    const runId = runIdRef.current;
    const currentFeedId = runFeedIdRef.current;
    if (!runId || !currentFeedId) {
      return;
    }
    try {
      const latest = await getRouteOverlapRun(currentFeedId, runId);
      if (runIdRef.current !== runId || runFeedIdRef.current !== currentFeedId) {
        return;
      }
      pollFailureCountRef.current = 0;
      setRun(latest);
      setToleranceMetersDefault(latest.config.toleranceMeters);
      if (!selectedComparisonRouteId && latest.comparisons.length > 0) {
        setSelectedComparisonRouteId(latest.comparisons[0].routeId);
      }
      if (isTerminalStatus(latest.status)) {
        stopPolling();
      }
    } catch (err) {
      if (runIdRef.current !== runId || runFeedIdRef.current !== currentFeedId) {
        return;
      }
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        stopPolling();
        runIdRef.current = null;
        runFeedIdRef.current = null;
        setRun(null);
        return;
      }
      pollFailureCountRef.current += 1;
      if (pollFailureCountRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        stopPolling();
      }
    }
  }, [selectedComparisonRouteId, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [poll, stopPolling]);

  const openModal = useCallback((routeId?: string | null) => {
    if (routeId) {
      setSelectedCandidateRouteId(routeId);
    }
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const focusComparisonRoute = useCallback(
    (routeId: string | null) => {
      setSelectedComparisonRouteId(routeId);
      if (routeId && run && feedId) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_route_overlap_result_focused, {
          feed_id: feedId,
          run_id: run.id,
          candidate_route_id: run.candidate.routeId,
          compared_route_id: routeId,
          version: scenarioId ?? version ?? 'base',
          timestamp: new Date().toISOString(),
        });
      }
    },
    [feedId, run, scenarioId, version],
  );

  const startRun = useCallback(
    async (request: CreateRouteOverlapRunRequest): Promise<RouteOverlapRun | null> => {
      if (!feedId) {
        setError('Select a hosted network before running overlap analysis.');
        return null;
      }
      setIsStarting(true);
      setError(null);
      setSelectedComparisonRouteId(null);
      try {
        const created = await createRouteOverlapRun(feedId, request);
        runIdRef.current = created.id;
        runFeedIdRef.current = feedId;
        runStartTimeRef.current = Date.now();
        pollFailureCountRef.current = 0;
        setRun(created);
        setToleranceMetersDefault(created.config.toleranceMeters);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_route_overlap_run_started, {
          feed_id: feedId,
          run_id: created.id,
          candidate_route_id: request.candidate.routeId,
          tolerance_meters: created.config.toleranceMeters,
          version: scenarioId ?? version ?? 'base',
          timestamp: new Date().toISOString(),
        });
        if (!isTerminalStatus(created.status)) {
          startPolling();
        }
        return created;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to start overlap analysis');
        return null;
      } finally {
        setIsStarting(false);
      }
    },
    [feedId, scenarioId, startPolling, version],
  );

  const cancelRun = useCallback(async () => {
    const runId = runIdRef.current;
    const currentFeedId = runFeedIdRef.current;
    stopPolling();
    if (!runId || !currentFeedId) {
      return;
    }
    try {
      const cancelled = await cancelRouteOverlapRun(currentFeedId, runId);
      if (runIdRef.current === runId) {
        setRun(cancelled);
      }
    } catch {
      if (runIdRef.current === runId) {
        setRun((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
      }
    }
  }, [stopPolling]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    const current: RouteOverlapRunStatus | 'idle' = run ? run.status : 'idle';
    if (current === prevStatusRef.current) {
      return;
    }
    prevStatusRef.current = current;
    if (!run || !feedId) {
      return;
    }
    if (current === 'complete') {
      if (run.comparisons.length > 0 && !selectedComparisonRouteId) {
        setSelectedComparisonRouteId(run.comparisons[0].routeId);
      }
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_route_overlap_run_completed, {
        feed_id: feedId,
        run_id: run.id,
        candidate_route_id: run.candidate.routeId,
        result_count: run.comparisons.length,
        top_overlap_pct: run.comparisons[0]?.candidateOverlapPct,
        duration_ms: runStartTimeRef.current ? Date.now() - runStartTimeRef.current : undefined,
        version: scenarioId ?? version ?? 'base',
        timestamp: new Date().toISOString(),
      });
    } else if (current === 'failed') {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_route_overlap_run_failed, {
        feature_area: 'fixed_route_overlap',
        error_category: run.error ? 'worker' : 'unknown',
        feed_id: feedId,
        run_id: run.id,
        candidate_route_id: run.candidate.routeId,
        version: scenarioId ?? version ?? 'base',
        timestamp: new Date().toISOString(),
      });
    } else if (current === 'cancelled') {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.fixed_route_overlap_run_cancelled, {
        feed_id: feedId,
        run_id: run.id,
        candidate_route_id: run.candidate.routeId,
        version: scenarioId ?? version ?? 'base',
        timestamp: new Date().toISOString(),
      });
    }
  }, [feedId, run, scenarioId, selectedComparisonRouteId, version]);

  const value = useMemo<RouteOverlapContextValue>(
    () => ({
      isModalOpen,
      selectedCandidateRouteId,
      setSelectedCandidateRouteId,
      openModal,
      closeModal,
      run,
      status: run ? run.status : 'idle',
      isStarting,
      error,
      toleranceMetersDefault,
      selectedComparisonRouteId,
      focusComparisonRoute,
      startRun,
      cancelRun,
      clearError,
    }),
    [
      cancelRun,
      clearError,
      closeModal,
      error,
      focusComparisonRoute,
      isModalOpen,
      isStarting,
      openModal,
      run,
      selectedCandidateRouteId,
      selectedComparisonRouteId,
      startRun,
      toleranceMetersDefault,
    ],
  );

  return <RouteOverlapContext.Provider value={value}>{children}</RouteOverlapContext.Provider>;
}

export function useRouteOverlap(): RouteOverlapContextValue {
  const ctx = useContext(RouteOverlapContext);
  if (!ctx) {
    throw new Error('useRouteOverlap must be used within a RouteOverlapProvider');
  }
  return ctx;
}
